import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Wand2, ArrowLeft, ShieldCheck, Download } from "lucide-react";

const CONTACT_EMAIL = "hello@brainideasfused.app";

const PAPERS = [
  {
    icon: ShieldCheck,
    title: "Security Audit",
    desc: "No auth surface, no data persistence risk, no third-party sharing, and session-scoped model usage — the full posture summary.",
    file: "/brainideasfused-security-audit.pdf",
    testid: "download-security-audit",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function TechSpec() {
  return (
    <div className="relative z-10 min-h-screen flex flex-col">
      <nav className="max-w-4xl w-full mx-auto flex items-center justify-between px-6 sm:px-10 py-6">
        <Link to="/" className="flex items-center gap-2.5" data-testid="techspec-home-link">
          <div className="h-8 w-8 rounded-lg bg-emerald-500 flex items-center justify-center">
            <Wand2 className="h-4 w-4 text-white" />
          </div>
          <span className="font-display text-lg font-medium tracking-tight">BrainIdeasFused</span>
        </Link>
        <Link
          to="/"
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors duration-200"
        >
          <ArrowLeft className="h-4 w-4" /> Back home
        </Link>
      </nav>

      <main className="max-w-4xl w-full mx-auto px-6 sm:px-10 pt-14 pb-20 flex-1">
        <motion.span
          variants={fadeUp} initial="hidden" animate="show"
          className="font-mono text-xs uppercase tracking-widest text-emerald-500"
        >
          Documentation
        </motion.span>
        <motion.h1
          variants={fadeUp} initial="hidden" animate="show" custom={1}
          className="font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05] mt-4"
        >
          Security audit.
        </motion.h1>
        <motion.p
          variants={fadeUp} initial="hidden" animate="show" custom={2}
          className="text-base sm:text-lg text-muted-foreground leading-relaxed mt-6 max-w-2xl"
        >
          A short PDF you can read or hand to anyone evaluating BrainIdeasFused — a plain-English
          summary of the app's security posture. Download, review, share.
        </motion.p>

        <div className="grid sm:grid-cols-2 gap-6 mt-12">
          {PAPERS.map((p, i) => (
            <motion.a
              key={p.title}
              href={p.file}
              download
              data-testid={p.testid}
              variants={fadeUp} initial="hidden" animate="show" custom={i + 3}
              className="group rounded-2xl border border-border bg-card p-8 flex flex-col hover:border-emerald-500/50 hover:-translate-y-0.5 transition-all duration-200"
            >
              <div className="h-12 w-12 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mb-6">
                <p.icon className="h-5 w-5 text-emerald-400" />
              </div>
              <h2 className="font-display text-xl font-medium tracking-tight mb-3">{p.title}</h2>
              <p className="text-base text-muted-foreground leading-relaxed flex-1">{p.desc}</p>
              <span className="inline-flex items-center gap-2 mt-6 font-mono text-xs uppercase tracking-widest text-emerald-400">
                <Download className="h-3.5 w-3.5" /> Download PDF
              </span>
            </motion.a>
          ))}
        </div>
      </main>

      <footer className="border-t border-border">
        <div className="max-w-4xl w-full mx-auto px-6 sm:px-10 py-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="flex items-center gap-2.5">
            <div className="h-7 w-7 rounded-lg bg-emerald-500 flex items-center justify-center">
              <Wand2 className="h-3.5 w-3.5 text-white" />
            </div>
            <span className="font-display font-medium tracking-tight">BrainIdeasFused</span>
          </div>
          <div className="flex items-center gap-8">
            <Link to="/app" className="font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors duration-200">
              Launch app
            </Link>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-emerald-400 transition-colors duration-200"
            >
              {CONTACT_EMAIL}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
